import type { PayloadAction } from '@reduxjs/toolkit';
import { all, call, delay, getContext, put, race, take, takeLatest } from 'redux-saga/effects';

import {
  type EnrichmentStats,
  hasActiveSession,
  type StatsRange,
} from '../../_shared/domain/stats.domain';
import { whenPageVisible } from '../../_shared/infrastructure/page-visibility';
import type { MetricsGateway } from '../infrastructure/metrics.gateway';
import { statsRequested, statsStopped } from './metrics.actions';
import { metricsActions } from './metrics.slice';

/** Poll cadence (ms) while at least one session is active. */
const STATS_POLL_MS = 10_000;

/**
 * Whether the figures can still move. The period's `running` count covers a
 * live session that fell out of the 20 `recent_sessions`; the list covers an
 * older backend without period totals.
 */
function stillRunning(stats: EnrichmentStats): boolean {
  return hasActiveSession(stats.recent_sessions) || (stats.period_totals?.running ?? 0) > 0;
}

/**
 * WORKER: fetch the period's stats, then poll every 10s while a session is
 * active (stops when idle or on error). All I/O via the injected gateway.
 */
function* pollStatsWorker(range: StatsRange) {
  const gateway: MetricsGateway = yield getContext('metricsGateway');
  while (true) {
    yield put(metricsActions.loadStarted());
    try {
      const stats = (yield call(gateway.fetchStats, range)) as EnrichmentStats;
      yield put(metricsActions.loadSucceeded(stats));
      if (!stillRunning(stats)) return;
    } catch {
      yield put(metricsActions.loadFailed());
      return;
    }
    yield delay(STATS_POLL_MS);
    yield call(whenPageVisible);
  }
}

/**
 * WORKER: run the stats poll for the requested period, cancelled by page
 * unmount (`statsStopped`) or by a newer request (`takeLatest`).
 */
function* statsPollLifecycle(action: PayloadAction<{ range: StatsRange }>) {
  const { range } = action.payload;
  yield put(metricsActions.rangeChanged(range));
  yield race({ poll: call(pollStatsWorker, range), stop: take(statsStopped.type) });
}

/** WATCHERS. */
export function* metricsSaga() {
  yield all([takeLatest(statsRequested.type, statsPollLifecycle)]);
}
